import { enqueueSnackbar } from 'notistack';
import React, { ReactNode, useCallback, useState } from 'react';

import { ActivityCalculatorSettings, ActivityCalculatorSettingsFull } from 'src/resources/api/activity-calculator/types.api';
import { getActivityCalculatorSettingsByIdAPI, getActivityCalculatorSettingsByMerchantCodeAPI, saveActivityCalculatorSettingsAPI, updateActivityCalculatorSettingsAPI } from 'src/resources/api/activity-calculator/index.api';

//
//

export type ActivityCalculatorSettingsState = {
  listOfActivityCalcSettings: ActivityCalculatorSettings[];
  isLoadingListOfActivityCalcSettings: boolean;
  getActivityCalculatorSettingsList: (merchantCode: string) => void;
  saveActivityCalculatorSettings: (merchantCode: string, settings: ActivityCalculatorSettingsFull) => void;
  currentSettings: ActivityCalculatorSettingsFull | null;
  isLoadingCurrentSettings: boolean;
  getCurrentSettingsById: (id: string) => void;
  updateActivityCalculatorSettings: (id: string, settings: ActivityCalculatorSettingsFull) => void;
};

export const ActivityCalculatorSettingsContext = React.createContext<ActivityCalculatorSettingsState>({
  listOfActivityCalcSettings: [],
  isLoadingListOfActivityCalcSettings: true,
  getActivityCalculatorSettingsList: (merchantCode: string) => {},
  saveActivityCalculatorSettings: (merchantCode: string, settings: ActivityCalculatorSettingsFull) => {},
  currentSettings: null,
  isLoadingCurrentSettings: true,
  getCurrentSettingsById: (id: string) => {},
  updateActivityCalculatorSettings: (id: string, settings: ActivityCalculatorSettingsFull) => {},
});

type ActivityCalculatorSettingsProviderProps = {
  children: ReactNode;
};

export const ActivityCalculatorSettingsContextProvider = ({ children }: ActivityCalculatorSettingsProviderProps) => {
  const [listOfSettings, setListOfSettings] = useState<ActivityCalculatorSettings[] | []>([]);
  const [isLoadingListOfSettings, setIsLoadingListOfSettings] = useState<boolean>(false);
  const [currentSettings, setCurrentSettings] = useState<ActivityCalculatorSettingsFull | null>(null);
  const [isLoadingCurrentSettings, setIsLoadingCurrentSettings] = useState<boolean>(false);

  const getActivityCalculatorSettingsList = useCallback((merchantCode: string) => {
    setIsLoadingListOfSettings(true);
    getActivityCalculatorSettingsByMerchantCodeAPI(merchantCode)
      .then((response) => {
        setListOfSettings(response);
      })
      .catch((response) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingListOfSettings(false);
      })
  },[]);

  const saveActivityCalculatorSettings = useCallback((merchantCode: string, settings: ActivityCalculatorSettingsFull) => {
    setIsLoadingCurrentSettings(true);
    saveActivityCalculatorSettingsAPI(merchantCode, settings)
      .then((response) => {
        enqueueSnackbar('Configuration is saved!', { variant: "success" });
        setCurrentSettings(response.data);
      })
      .catch((response) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[]);

  const getCurrentSettingsById = useCallback((id: string) => {
    setIsLoadingCurrentSettings(true);
    getActivityCalculatorSettingsByIdAPI(id)
      .then((response) => {
        setCurrentSettings(response);
      })
      .catch((response) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[]);

  const updateActivityCalculatorSettings = useCallback((id: string, settings: ActivityCalculatorSettingsFull) => {
    setIsLoadingCurrentSettings(true);
    updateActivityCalculatorSettingsAPI(id, settings)
      .then((response) => {
        setCurrentSettings({ ...response, id });
        enqueueSnackbar('Configuration is updated', { variant: "success" });
      })
      .catch((response) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[]);

  return (
    <ActivityCalculatorSettingsContext.Provider
      value={{
        listOfActivityCalcSettings: listOfSettings,
        isLoadingListOfActivityCalcSettings: isLoadingListOfSettings,
        getActivityCalculatorSettingsList,
        saveActivityCalculatorSettings,
        isLoadingCurrentSettings,
        currentSettings,
        getCurrentSettingsById,
        updateActivityCalculatorSettings
      }}
    >
      {children}
    </ActivityCalculatorSettingsContext.Provider>
  );
};
